import { useQuery } from '@tanstack/react-query'

import { type AppTimezonePreference } from '@sparkai/utils'

import { apiClient } from '../apiClient'
import {
  getOperatorPerformanceQueryTimezonePart,
  getOperatorPerformanceRequestHeaders,
} from './operatorPerformanceTimezone'

export const OPERATOR_PROGRAM_INSTANCE_STATS_KEY = 'operator-program-instance-stats'

export type GetProgramInstanceStatsRequest = {
  operatorId?: string
  programIds: string[]
  startDate: string
  endDate: string
  timezonePreference?: AppTimezonePreference
}

type ProgramInstanceMetric = {
  mean: number | null
  trend: number | null
}

type ProgramInstanceVolumeMetric = {
  mean: number
  trend: number
}

export type ProgramInstanceStatsBreakdown = {
  taskCount: number
  accuracy: number | null
}

export type OperatorProgramInstanceStats = {
  programInstanceId: string | null
  liveProgramId: string
  recyclerProgramId: string | null
  breakdown: {
    overall: ProgramInstanceStatsBreakdown
    live: ProgramInstanceStatsBreakdown
    recycled: ProgramInstanceStatsBreakdown
  }
  volume: ProgramInstanceVolumeMetric & {
    total: number
  }
  accuracy: ProgramInstanceMetric
  responseTime: ProgramInstanceMetric
}

export type GetProgramInstanceStatsResponse = {
  programs: OperatorProgramInstanceStats[]
} | null

export async function fetchProgramInstanceStats({
  operatorId,
  programIds,
  startDate,
  endDate,
  timezonePreference,
}: GetProgramInstanceStatsRequest) {
  const headers = getOperatorPerformanceRequestHeaders(timezonePreference)
  const { data } = await apiClient.get<GetProgramInstanceStatsResponse>(
    `/int/v3/operators/${operatorId}/program-instances/stats`,
    {
      params: { startDate, endDate, programIds: programIds.join() },
      ...(headers ? { headers } : {}),
    }
  )
  return data
}

export function useGetProgramInstanceStats({
  operatorId,
  programIds,
  startDate,
  endDate,
  timezonePreference,
}: GetProgramInstanceStatsRequest) {
  const timezoneQueryKeyPart = getOperatorPerformanceQueryTimezonePart(timezonePreference)

  return useQuery({
    queryKey: [
      OPERATOR_PROGRAM_INSTANCE_STATS_KEY,
      operatorId,
      startDate,
      endDate,
      programIds,
      ...(timezoneQueryKeyPart ? [timezoneQueryKeyPart] : []),
    ],
    queryFn: () =>
      fetchProgramInstanceStats({ operatorId, programIds, startDate, endDate, timezonePreference }),
    enabled: Boolean(operatorId) && programIds.length > 0,
    refetchOnWindowFocus: false,
  })
}
